import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Heading } from '../../layout';
import { useUIColors } from '../../../hooks/useTheme';
import PurchaseOrdersTable from '../../purchaseOrders/PurchaseOrdersTable';
import PurchaseOrderDetailModal from '../../placeholders/PurchaseOrderDetailModal';
import PurchaseOrderInvoicingModal from '../../placeholders/PurchaseOrderInvoicingModal';
import FinancePurchasePaymentsTable from './FinancePurchasePaymentsTable';
import FinancePurchasePaymentsCharts from './FinancePurchasePaymentsCharts';
import purchaseOrdersAPI from '../../../services/purchaseOrdersAPI';
import {
  buildPoPaymentScheduleRows,
  resolvePoWorkflowStatus,
  normalizePoStatus,
  poAmountClp,
} from '../../placeholders/purchaseOrderSummary';

const formatSummaryMoney = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const FinancePurchasesWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const [listTab, setListTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailOrder, setDetailOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [actionPending, setActionPending] = useState(false);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [data, items] = await Promise.all([
        purchaseOrdersAPI.getAll(),
        purchaseOrdersAPI.getAllItems().catch(() => []),
      ]);
      setOrders(Array.isArray(data) ? data : []);
      setOrderItems(Array.isArray(items) ? items : []);
    } catch (err) {
      setError(err?.message || 'No se pudieron cargar las órdenes de compra.');
      setOrders([]);
      setOrderItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const pendingApprovalOrders = useMemo(
    () => orders.filter((order) => resolvePoWorkflowStatus(order) === 'Por Aprobar'),
    [orders]
  );

  const paymentSchedule = useMemo(() => buildPoPaymentScheduleRows(orders), [orders]);

  const openPaymentRows = useMemo(
    () => paymentSchedule.filter((row) => row.bucket !== 'pagado'),
    [paymentSchedule]
  );

  const overduePaymentAmount = useMemo(
    () =>
      paymentSchedule
        .filter((row) => row.bucket === 'vencido')
        .reduce((acc, row) => acc + Number(row.payment_amount || 0), 0),
    [paymentSchedule]
  );

  const listTabs = useMemo(
    () => [
      { key: 'orders', label: 'Órdenes de compra', count: orders.length },
      { key: 'approval', label: 'Por aprobar', count: pendingApprovalOrders.length },
      { key: 'payments', label: 'Pagos', count: openPaymentRows.length },
    ],
    [orders.length, pendingApprovalOrders.length, openPaymentRows.length]
  );

  const tabRows = useMemo(() => {
    if (listTab === 'approval') return pendingApprovalOrders;
    return orders;
  }, [listTab, orders, pendingApprovalOrders]);

  const listSubtitle = useMemo(() => {
    if (loading) return 'Cargando…';
    if (error) return error;
    if (listTab === 'approval') {
      const amount = pendingApprovalOrders.reduce((acc, order) => acc + poAmountClp(order), 0);
      return `${pendingApprovalOrders.length} pendiente${pendingApprovalOrders.length === 1 ? '' : 's'} · ${formatSummaryMoney(amount)}`;
    }
    if (listTab === 'payments') {
      const pendingAmount = openPaymentRows.reduce((acc, row) => acc + Number(row.payment_amount || 0), 0);
      return (
        `${openPaymentRows.length} por pagar · ${formatSummaryMoney(pendingAmount)}` +
        (overduePaymentAmount > 0 ? ` · vencido ${formatSummaryMoney(overduePaymentAmount)}` : '') +
        ' · emisión factura (o OC) + condición de pago'
      );
    }
    const amount = orders.reduce((acc, order) => acc + poAmountClp(order), 0);
    return `${orders.length} órdenes · ${formatSummaryMoney(amount)}`;
  }, [
    error,
    listTab,
    loading,
    openPaymentRows,
    orders,
    overduePaymentAmount,
    pendingApprovalOrders,
  ]);

  const openDetail = async (row) => {
    const poId = Number(row?.id || 0);
    if (!poId) return;
    setDetailLoading(true);
    try {
      const full = await purchaseOrdersAPI.getById(poId);
      setDetailOrder(full);
    } catch (err) {
      window.alert(err?.message || 'No se pudo cargar la orden.');
    } finally {
      setDetailLoading(false);
    }
  };

  const openInvoicing = async (row) => {
    const poId = Number(row?.id || 0);
    if (!poId) return;
    setDetailLoading(true);
    try {
      const full = await purchaseOrdersAPI.getById(poId);
      setInvoiceOrder(full);
    } catch (err) {
      window.alert(err?.message || 'No se pudo cargar la orden para facturación.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = async (row) => {
    const poId = Number(row?.id || 0);
    if (!poId || actionPending) return;
    const code = row?.po_code || poId;
    if (!window.confirm(`¿Aprobar la orden ${code}?`)) return;
    setActionPending(true);
    try {
      await purchaseOrdersAPI.approve(poId);
      await loadOrders();
    } catch (err) {
      window.alert(err?.message || 'No se pudo aprobar la orden.');
    } finally {
      setActionPending(false);
    }
  };

  const handleReject = async (row) => {
    const poId = Number(row?.id || 0);
    if (!poId || actionPending) return;
    const code = row?.po_code || poId;
    if (!window.confirm(`¿Rechazar la aprobación de ${code}? Volverá a estado Borrador.`)) return;
    setActionPending(true);
    try {
      await purchaseOrdersAPI.reject(poId);
      await loadOrders();
    } catch (err) {
      window.alert(err?.message || 'No se pudo rechazar la orden.');
    } finally {
      setActionPending(false);
    }
  };

  const handleMarkPaid = async (row) => {
    const poId = Number(row?.id || 0);
    if (!poId || actionPending) return;
    const code = row?.po_code || poId;
    const late =
      normalizePoStatus(row.status) === 'Por Pagar Atrasado' || row.bucket === 'vencido';
    const label = late ? 'pagada atrasada' : 'pagada a tiempo';
    if (!window.confirm(`¿Marcar la orden ${code} como ${label}?`)) return;
    setActionPending(true);
    try {
      await purchaseOrdersAPI.markPaid(poId, { late });
      await loadOrders();
    } catch (err) {
      window.alert(err?.message || 'No se pudo marcar el pago.');
    } finally {
      setActionPending(false);
    }
  };

  const handleSaveInvoicing = async (payload) => {
    if (!invoiceOrder?.id) return;
    setInvoiceSaving(true);
    try {
      await purchaseOrdersAPI.updateInvoicing(invoiceOrder.id, payload);
      setInvoiceOrder(null);
      await loadOrders();
    } catch (err) {
      window.alert(err?.message || 'No se pudo guardar la facturación.');
    } finally {
      setInvoiceSaving(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: 6 }}>
          Finanzas · Compras
        </Heading>
        <p style={{ margin: 0, maxWidth: 720, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
          Revisa órdenes de compra, aprueba las pendientes y programa pagos según emisión de factura (o
          fecha OC) más la condición de pago.
        </p>
      </div>

      <div style={{ marginBottom: 16 }}>
        <FinancePurchasePaymentsCharts orders={orders} orderItems={orderItems} />
      </div>

      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: ui.page,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>Órdenes de compra</h3>
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>{listSubtitle}</p>
          </div>
        </div>

        <div
          role="tablist"
          aria-label="Vistas de compras"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: '0 12px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            overflowX: 'auto',
          }}
        >
          {listTabs.map((item) => {
            const active = listTab === item.key;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setListTab(item.key)}
                style={{
                  appearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '10px 12px',
                  marginBottom: -1,
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  color: active ? ui.text : ui.muted,
                  borderBottom: active ? '2px solid #16a34a' : '2px solid transparent',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>{item.label}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: active ? (isDarkMode ? '#86efac' : '#15803d') : ui.muted,
                    background: active
                      ? isDarkMode
                        ? 'rgba(22, 163, 74, 0.2)'
                        : 'rgba(22, 163, 74, 0.12)'
                      : isDarkMode
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(0,0,0,0.05)',
                    borderRadius: 999,
                    padding: '1px 7px',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {item.count}
                </span>
              </button>
            );
          })}
        </div>

        {listTab === 'payments' ? (
          <FinancePurchasePaymentsTable
            orders={orders}
            loading={loading || detailLoading || actionPending}
            error={error}
            isDarkMode={isDarkMode}
            onRowClick={openDetail}
            onMarkPaid={handleMarkPaid}
            onInvoice={openInvoicing}
          />
        ) : (
          <PurchaseOrdersTable
            rows={tabRows}
            loading={loading || detailLoading || actionPending}
            error={error}
            isDarkMode={isDarkMode}
            hideChrome
            tableTitle="Órdenes de compra"
            onRowClick={openDetail}
            onApprove={listTab === 'approval' ? handleApprove : undefined}
            onReject={listTab === 'approval' ? handleReject : undefined}
            onInvoice={listTab === 'orders' ? openInvoicing : undefined}
          />
        )}
      </div>

      <PurchaseOrderDetailModal
        isOpen={Boolean(detailOrder)}
        onClose={() => setDetailOrder(null)}
        purchaseOrder={detailOrder}
        isDarkMode={isDarkMode}
      />

      <PurchaseOrderInvoicingModal
        isOpen={Boolean(invoiceOrder)}
        onClose={() => !invoiceSaving && setInvoiceOrder(null)}
        purchaseOrder={invoiceOrder}
        isDarkMode={isDarkMode}
        isSaving={invoiceSaving}
        onSave={handleSaveInvoicing}
      />
    </div>
  );
};

export default FinancePurchasesWorkspace;
